import React from 'react';

function TankList({ tanks, selectedType, displayMode, onTankSelect, onAddToCart }) {
  if (!tanks || tanks.length === 0) {
    return <p className="tw-p-6 tw-text-center tw-text-gray-500">No tanks match the current filters.</p>;
  }

  const getImageUrl = (tank) => {
    return tank.image;
  };

  const getInventoryInfo = (tank) => {
    if (tank.inventoryCount > 0) {
      return { primary: `${tank.inventoryCount} In Stock`, secondary: `Ships 1-2 days` };
    }
    let shippingEstimate = '1-2 weeks';
    const vendor = tank.vendor ? tank.vendor.toLowerCase() : '';
    if (vendor.includes('icon direct')) shippingEstimate = '2-4 days';
    else if (vendor.includes('alpha')) shippingEstimate = '1-2 weeks';
    else if (vendor.includes('elkhart plastics') || vendor.includes('ameri-kart')) shippingEstimate = '3-4 weeks';
    return { primary: "CUSTOM MADE", secondary: `Ships in ${shippingEstimate}` };
  };

  const handleBuyButtonClick = (e, tank) => {
    e.stopPropagation();
    if (tank && tank.variantId) {
      onAddToCart(tank.variantId);
    } else {
      console.error("TankList: Cannot add to cart. Tank data or Variant ID is missing.", tank);
      alert("There was an issue preparing this item for your cart. Please ensure product details are complete.");
    }
  };

  const pillBaseStyle = "tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded-full tw-inline-block"; // For consistent pill look
  const inStockPillStyle = `${pillBaseStyle} tw-text-sm tw-bg-green-100 tw-text-green-700`;
  const madeToOrderPillStyle = `${pillBaseStyle} tw-text-amber-700`; // Optional for MADE TO ORDER

  // ------- RENDER GRID VIEW ------- 
  const renderGridView = () => (
    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-4 tw-pt-2">
      {tanks.map((tank, index) => {
        const imageUrl = getImageUrl(tank);
        const inventoryInfo = getInventoryInfo(tank);
        const isInStock = inventoryInfo.primary.includes("In Stock");
        return (
          <div 
            key={`${tank.id}-grid`} 
            className="tw-border tw-border-gray-200 tw-rounded-md tw-p-3 tw-flex tw-flex-col tw-items-center tw-bg-white hover:tw-shadow-md tw-cursor-pointer"
            onClick={() => onTankSelect(index)}
          >
            <div className="tw-w-24 tw-h-24 tw-mb-2">
              <img src={imageUrl} alt={`${tank.gallons} Gallon Tank`} className="tw-w-full tw-h-full tw-object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{tank.gallons} Gallons</p>
            <p className="tw-text-xs tw-text-gray-500">{tank.length}"x{tank.width}"x{tank.height}"</p>
            <p className="tw-text-md tw-font-bold tw-text-gray-800 tw-my-1">${tank.price ? tank.price.toFixed(2) : 'N/A'}</p>
            <div className="tw-mb-1 tw-text-center"> {/* Container for pill and secondary text */}
                <span className={isInStock ? inStockPillStyle : madeToOrderPillStyle}>{inventoryInfo.primary}</span>
                <p className="tw-text-xs tw-text-gray-500">{inventoryInfo.secondary}</p>
            </div>
            <button 
              onClick={(e) => handleBuyButtonClick(e, tank)}
              className="tw-w-full tw-mt-auto tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-bg-brand-red hover:tw-bg-brand-red-dark tw-text-white tw-rounded"
            >BUY</button>
          </div>
        );
      })}
    </div>
  );

  
  // ------- RENDER LIST VIEW ------- 
  const renderListView = () => (
    <ul className="tw-border-t tw-border-gray-200">
      {tanks.map((tank, index) => {
        const imageUrl = getImageUrl(tank);
        const inventoryInfo = getInventoryInfo(tank);
        const isInStock = inventoryInfo.primary.includes("In Stock");
        return (
          <li 
            key={`${tank.id}-list`}
            className="tw-flex tw-items-center tw-py-4 tw-px-2 tw-border-b tw-border-black hover:tw-bg-gray-50 tw-cursor-pointer"
            onClick={() => onTankSelect(index)}
          >
            <div className="tw-flex-shrink-0 tw-w-12 tw-h-16 sm:tw-w-16 sm:tw-h-16 md:tw-w-20 md:tw-h-20 tw-mr-2 sm:tw-mr-3">
              <img src={imageUrl} alt={`${tank.gallons} Gallon Tank`} className="tw-w-full tw-h-full tw-object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <div className="tw-flex-grow tw-min-w-0 tw-mr-3">
              <p className="tw-text-md tw-font-semibold tw-text-gray-800">{tank.gallons} Gal</p>
              <p className="tw-text-sm tw-text-gray-800">{tank.length}" x {tank.width}" x {tank.height}"</p>
            </div>
            <div className="tw-flex-shrink-0 tw-text-xs tw-mr-3 tw-pr-2 tw-text-center">
              <span className={isInStock ? inStockPillStyle : madeToOrderPillStyle}>{inventoryInfo.primary}</span>
              <p className={`tw-text-gray-500 ${isInStock ? 'tw-mt-1' : ''}`}>{inventoryInfo.secondary}</p> {/* Add margin only if pill is present */}
            </div>
            <div className="tw-flex-shrink-0 tw-flex tw-flex-col tw-items-center">
              <p className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-1">${tank.price ? tank.price.toFixed(2) : 'N/A'}</p>
              <button 
                onClick={(e) => handleBuyButtonClick(e, tank)}
                className="tw-px-2 tw-py-1 tw-text-xs tw-font-bold tw-bg-brand-red hover:tw-bg-brand-red-dark tw-text-white tw-rounded"
              >BUY NOW</button>
            </div>
          </li>
        );
      })}
    </ul>
  );

  // ------- RENDER COMPARE VIEW ------- 
  const renderCompareView = () => (
    <div className="tw-overflow-x-auto tw-pt-2">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200 tw-border tw-border-gray-200">
        <thead className="tw-bg-gray-50">
          <tr>
            <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tracking-wider">Gal</th>
            <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tracking-wider">L</th>
            <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tracking-wider">W</th>
            <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tracking-wider">H</th>
            <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tracking-wider">Stock</th>
            <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tracking-wider">Price</th>
            <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tracking-wider">Buy</th>
          </tr>
        </thead>
        <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
          {tanks.map((tank, index) => {
            const inventoryInfo = getInventoryInfo(tank);
            const isInStock = inventoryInfo.primary.includes("In Stock");
            return (
              <tr 
                key={`${tank.id}-compare`} 
                onClick={() => onTankSelect(index)}
                className="hover:tw-bg-gray-50 tw-cursor-pointer"
              >
                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-sm tw-text-gray-700">{tank.gallons}</td>
                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-sm tw-text-gray-700">{tank.length}</td>
                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-sm tw-text-gray-700">{tank.width}</td>
                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-sm tw-text-gray-700">{tank.height}</td>
                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-xs">
                    <span className={isInStock ? inStockPillStyle : madeToOrderPillStyle}>{inventoryInfo.primary}</span>
                    <p className={`tw-text-gray-500 ${isInStock ? 'tw-mt-0.5' : ''}`}>{inventoryInfo.secondary}</p>
                </td>
                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-text-gray-800">${tank.price ? tank.price.toFixed(2) : 'N/A'}</td>
                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                  <button 
                    onClick={(e) => handleBuyButtonClick(e, tank)}
                    className="tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-bg-brand-red hover:tw-bg-brand-red-dark tw-text-white tw-rounded"
                  >BUY</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // Main return for TankList component
  if (!tanks || tanks.length === 0) { // This check might be redundant if App.js handles it, but good as a safeguard
    // This message is now typically handled by App.js before TankList is even rendered if filteredTanks is empty.
    // However, if TankList is rendered with an empty tanks array for some other reason:
    return <p className="tw-p-6 tw-text-center tw-text-gray-500">No tanks found matching your criteria.</p>;
  }
  
  // The "XX matching tanks" header is now in App.js
  // Conditional rendering based on displayMode
  if (displayMode === 'grid') {
    return renderGridView();
  } else if (displayMode === 'compare') {
    return renderCompareView();
  } else { // Default to 'list'
    return renderListView();
  }
}

export default TankList;
